import React, { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa6";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import WaveSurfer from "wavesurfer.js";

const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const AudioPlayer = ({ audioUrl }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState("0:00");
  const [currentTime, setCurrentTime] = useState("0:00");

  useEffect(() => {
    let wavesurfer = null;
    let isSubscribed = true;

    const initWaveSurfer = async () => {
      if (audioUrl && waveformRef.current && !wavesurferRef.current) {
        setIsLoading(true);
        try {
          wavesurfer = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: "#000000",
            progressColor: "#bc1535",
            cursorColor: "#bc1535",
            barWidth: 2,
            barGap: 3,
            height: 40,
            responsive: true,
            barRadius: 3,
            cursorWidth: 2,
            interact: true,
            backend: "MediaElement",
            mediaControls: false,
            normalize: true,
            xhr: {
              cache: true,
              mode: "cors",
              method: "GET",
              credentials: "same-origin",
              headers: [
                { key: "cache-control", value: "public, max-age=31536000" },
              ],
            },
          });

          wavesurferRef.current = wavesurfer;

          wavesurfer.on("loading", (progress) => {
            if (isSubscribed) {
              setIsLoading(progress < 100);
            }
          });

          wavesurfer.on("ready", () => {
            if (isSubscribed) {
              setDuration(formatTime(wavesurfer.getDuration()));
              setIsLoading(false);
            }
          });

          wavesurfer.on("audioprocess", () => {
            if (isSubscribed) {
              setCurrentTime(formatTime(wavesurfer.getCurrentTime()));
            }
          });

          wavesurfer.on("finish", () => {
            if (isSubscribed) {
              setIsPlaying(false);
            }
          });

          await wavesurfer.load(audioUrl);
        } catch (error) {
          console.error("Error initializing WaveSurfer:", error);
          setIsLoading(false);
        }
      }
    };

    initWaveSurfer();

    return () => {
      isSubscribed = false;
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!isLoading && wavesurferRef.current) {
      wavesurferRef.current.playPause();
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeToggle = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={handlePlayPause}
          disabled={isLoading}
          className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
            isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"
          }`}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <FaPause className="text-sm" />
          ) : (
            <FaPlay className="text-sm ml-0.5" />
          )}
        </button>
        <div className="text-xs font-medium text-gray-600">
          {currentTime} / {duration}
        </div>
        <button
          onClick={handleVolumeToggle}
          className="ml-auto text-gray-600 hover:text-[#bc1535]"
        >
          {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>
      </div>
      <div ref={waveformRef} className="w-full rounded overflow-hidden" />
    </div>
  );
};
